import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Select } from "@/components/ui/select";
import { Toolbar } from "@/components/ui/toolbar";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "@/hooks/use-api-keys";
import type { ApiKey, ApiKeyRole } from "@/types";
import { Check, Copy, KeyRound, Plus, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

function formatDate(value: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

const ROLE_LABELS: Record<ApiKeyRole, string> = {
  read: "Read-only",
  read_write: "Read + write",
};

const MCP_URL =
  import.meta.env.VITE_MCP_URL ||
  `${(import.meta.env.VITE_API_URL || window.location.origin).replace(/\/$/, "")}/mcp/`;

const MCP_CLIENTS = [
  {
    id: "claude-code",
    label: "Claude Code",
    hint: "Run in a terminal. Add --scope project to write it into a checked-in .mcp.json.",
    snippet: `claude mcp add --transport http asterisk ${MCP_URL} \\
  --header "Authorization: Bearer <api-key>"`,
  },
  {
    id: "claude-desktop",
    label: "Claude Desktop",
    hint: "claude_desktop_config.json — restart the app after editing.",
    snippet: `{
  "mcpServers": {
    "asterisk": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer <api-key>" }
    }
  }
}`,
  },
  {
    id: "codex",
    label: "Codex CLI",
    hint: "~/.codex/config.toml — then export ASTERISK_MCP_KEY=<api-key>.",
    snippet: `[mcp_servers.asterisk]
url = "${MCP_URL}"
bearer_token_env_var = "ASTERISK_MCP_KEY"
startup_timeout_sec = 20`,
  },
  {
    id: "opencode",
    label: "OpenCode",
    hint: 'opencode.json — "oauth": false matters, or a bad key surfaces as an OAuth error.',
    snippet: `{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "asterisk": {
      "type": "remote",
      "url": "${MCP_URL}",
      "enabled": true,
      "headers": { "Authorization": "Bearer {env:ASTERISK_MCP_KEY}" },
      "oauth": false
    }
  }
}`,
  },
  {
    id: "curl",
    label: "curl",
    hint: "Smoke test — lists exactly the tools this key can see.",
    snippet: `curl -s -X POST ${MCP_URL} \\
  -H "Authorization: Bearer <api-key>" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json, text/event-stream" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}' | jq '.result.tools[].name'`,
  },
] as const;

type McpClientId = (typeof MCP_CLIENTS)[number]["id"];

const ROLE_HELP: Record<ApiKeyRole, string> = {
  read: "Inspection only: trunk/dialplan/call listings, logs, config, and Asterisk CLI commands limited to show/list/get.",
  read_write: "Full access: create and delete trunks, edit the dialplan, reload config, and originate calls.",
};

export default function ApiKeysPage() {
  const { data, isLoading, error, refetch, isFetching } = useApiKeys();
  const createMut = useCreateApiKey();
  const revokeMut = useRevokeApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState<ApiKeyRole>("read_write");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);
  const [client, setClient] = useState<McpClientId>("claude-code");

  const activeClient = MCP_CLIENTS.find((c) => c.id === client) ?? MCP_CLIENTS[0];

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((k) => k.name?.toLowerCase().includes(q));
  }, [list, query]);

  const handleCreate = () => {
    if (!name.trim() || createMut.isPending) return;
    createMut.mutate(
      { name: name.trim(), role },
      {
        onSuccess: (data) => {
          setCreatedKey(data.api_key);
          setName("");
          toast.success(`API key created (${ROLE_LABELS[data.role]})`);
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      }
    );
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreatedKey(null);
    setCopied(false);
    setName("");
    setRole("read_write");
  };

  const handleRevoke = (key: ApiKey) => {
    if (key.revoked) return;
    setPendingRevoke(key);
  };

  const columns: Column<ApiKey>[] = [
    { key: "name", header: "Name" },
    {
      key: "key_prefix",
      header: "Key",
      render: (k) => <span className="font-mono text-xs">{k.key_prefix}…</span>,
    },
    {
      key: "role",
      header: "Role",
      render: (k) => (
        <Badge variant={k.role === "read" ? "secondary" : "default"}>
          {ROLE_LABELS[k.role] ?? k.role}
        </Badge>
      ),
    },
    {
      key: "revoked",
      header: "Status",
      render: (k) =>
        k.revoked ? (
          <Badge variant="destructive">Revoked</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        ),
    },
    { key: "created_at", header: "Created", render: (k) => formatDate(k.created_at) },
    { key: "last_used_at", header: "Last used", render: (k) => formatDate(k.last_used_at) },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="MCP API Keys"
        description={<>Keys let AI agents reach the MCP server at <span className="font-mono">/mcp</span>. Each key is read-only or read + write.</>}
      />

      <Toolbar
        search={{ value: query, onChange: setQuery, placeholder: "Search keys..." }}
        count={`${filtered.length}${query ? ` of ${list.length}` : ""}`}
        onRefresh={() => refetch()}
        refreshing={isFetching}
        actions={
          <Button onClick={() => setCreateOpen(true)} size="sm" className="gap-1.5">
            <Plus size={14} />
            Create Key
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        getRowKey={(k) => k.id}
        onDelete={handleRevoke}
        emptyMessage={query ? "No matches for your search." : "No API keys yet. Create one to connect an MCP client."}
        error={error}
        onRetry={() => refetch()}
      />

      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <KeyRound size={14} className="text-muted-foreground" />
            Connect an MCP client
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          <p className="text-sm text-muted-foreground">
            Any streamable-HTTP MCP client works. Point it at{" "}
            <span className="font-mono">{MCP_URL}</span> (keep the trailing slash) and
            send <span className="font-mono">Authorization: Bearer &lt;api-key&gt;</span> — or{" "}
            <span className="font-mono">X-API-Key</span> if the client cannot set an Authorization
            header. Every request is a POST; there is no OAuth.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {MCP_CLIENTS.map((c) => (
              <Button
                key={c.id}
                size="sm"
                variant={client === c.id ? "default" : "outline"}
                onClick={() => setClient(c.id)}
              >
                {c.label}
              </Button>
            ))}
          </div>

          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">{activeClient.hint}</p>
            <pre className="bg-muted rounded-md p-3 text-xs font-mono overflow-x-auto">
              {activeClient.snippet}
            </pre>
          </div>

          <p className="text-sm text-muted-foreground">
            A read-only key only sees the inspection tools — write tools are hidden from its tool
            list and rejected if called. See <span className="font-mono">docs/mcp.md</span> for
            Cursor, stdio-only clients via <span className="font-mono">mcp-remote</span>, raw SDK
            usage, and troubleshooting.
          </p>
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onClose={closeCreate}
        title={createdKey ? "API Key Created" : "Create API Key"}
        className="max-w-md"
      >
        {createdKey ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
              <TriangleAlert size={16} className="mt-0.5 shrink-0 text-warning" />
              <span>
                Copy this key now. It is shown <strong>only once</strong> and cannot be
                retrieved later.
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              Access: <strong>{ROLE_LABELS[role]}</strong>. {ROLE_HELP[role]}
            </p>
            <div className="flex gap-2">
              <Input readOnly value={createdKey} className="font-mono text-xs" />
              <Button onClick={handleCopy} size="sm" variant="outline" className="gap-1.5 shrink-0">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button onClick={closeCreate} size="sm">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="claude-code"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                A label to identify who or what uses this key.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Access *</Label>
              <Select value={role} onChange={(e) => setRole(e.target.value as ApiKeyRole)}>
                <option value="read_write">{ROLE_LABELS.read_write}</option>
                <option value="read">{ROLE_LABELS.read}</option>
              </Select>
              <p className="text-xs text-muted-foreground">{ROLE_HELP[role]}</p>
              <p className="text-xs text-muted-foreground">
                Access is fixed at creation. To change it, revoke this key and create a new one.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={closeCreate} size="sm" variant="outline">
                Cancel
              </Button>
              <Button onClick={handleCreate} size="sm" disabled={!name.trim() || createMut.isPending}>
                Create
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        title="Revoke API key"
        description={<>Revoke API key <strong>{pendingRevoke?.name}</strong>? MCP clients using it will lose access immediately.</>}
        confirmLabel="Revoke"
        loading={revokeMut.isPending}
        onConfirm={() =>
          pendingRevoke &&
          revokeMut.mutate(pendingRevoke.id, {
            onSuccess: () => { toast.success("API key revoked"); setPendingRevoke(null); },
            onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
          })
        }
      />
    </div>
  );
}
